import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FlaskConical, ArrowRight, ChevronDown, Syringe, ShieldCheck, Zap, Brain, Scale, Clock, HeartPulse, Flame, Sparkles, Calculator, BookOpen, ChevronRight, TrendingUp, Star, ExternalLink, Activity, Download } from 'lucide-react';
import { articlesApi } from '../lib/api';
import { PageSEO } from '../components/seo/SchemaMarkup';
import { ShareButton } from '../components/ShareButton';
import AprProductGrid from '../components/apr/AprProductGrid';
import { PeptiCoolBanner } from '../components/pepticool/PeptiCoolBanner';
import { LocatorFeeder } from '../components/LocatorFeeder';
import { APR_TOP_SELLERS } from '../data/aprProducts';

// ───────────────── Category Data ─────────────────

const CATEGORIES = [
  { id: 'getting-started', label: 'Getting Started', icon: BookOpen, color: '#00F0FF', desc: 'New to peptides? Start here.' },
  { id: 'recovery', label: 'Recovery & Healing', icon: HeartPulse, color: '#22c55e', desc: 'BPC-157, TB-500, KPV' },
  { id: 'growth-hormone', label: 'Growth Hormone', icon: TrendingUp, color: '#a78bfa', desc: 'CJC-1295, Ipamorelin' },
  { id: 'fat-loss', label: 'Fat Loss & Metabolic', icon: Flame, color: '#f59e0b', desc: 'Retatrutide, AOD-9604, Tesamorelin' },
  { id: 'longevity', label: 'Anti-Aging & Longevity', icon: Sparkles, color: '#E63946', desc: 'MOTS-C, Epitalon, GHK-Cu' },
  { id: 'cognitive', label: 'Brain & Cognitive', icon: Brain, color: '#818cf8', desc: 'Dihexa, Semax, Selank' },
];

// Article → category mapping
const ARTICLE_CATEGORIES = {
  'getting-started': [
    'peptide-side-effects-what-the-research-actually-says',
    'the-complete-peptide-guide',
    'blood-tests-before-peptides-the-complete-pre-protocol-lab-guide',
    'how-to-reconstitute-peptides',
    'peptide-timing-guide',
    'are-peptides-steroids',
    'the-complete-guide-to-peptide-therapy-for-athletes',
    'peptides-for-women-dosing-cycle-timing-safety-guide',
    'why-mixing-peptides-sabotages-results',
    'why-glow-and-klow-peptide-blends-are-dead-on-arrival',
  ],
  'recovery': [
    'bpc-157-dosage-guide',
    'the-wolverine-complex-the-ultimate-peptide-stack-for-rapid-recovery-and-healing',
    'tb500-heart-health-cardiac-repair',
    'kpv-peptide-master-anti-inflammatory-guide',
    'fibromyalgia-solution',
  ],
  'growth-hormone': [
    'cjc-1295-with-dac-ipamorelin-how-these-2-peptides-rebuild-your-body-from-the-inside-out',
    'slu-pp-332-guide-the-exercise-mimetic-compound-why-bac-water-destroys-it',
  ],
  'fat-loss': [
    'retatrutide-cancer-prevention-science',
    'retatrutide-longevity-weapon-epigenetic-reversal-cancer-defense',
    'aod-9604-fat-burning-peptide-guide',
    'tesamorelin-visceral-fat-peptide-guide',
  ],
  'longevity': [
    'mots-c-benefits',
    'mots-c-vs-metformin-longevity-comparison',
    'epitalon-telomere-anti-aging-peptide-guide',
  ],
  'cognitive': [
    'dihexa-peptide-brain-repair-what-the-science-actually-says',
  ],
};

const PEPTIDE_TOOLS = [
  { title: 'Peptide Dosing Calculator', desc: 'Calculate exact doses based on your vial concentration and target dose.', icon: Calculator, href: '/tools/peptide-dosing', color: '#E63946' },
  { title: 'Peptide Comparison Tool', desc: 'Compare 2-3 peptides side-by-side: dosing, effects, cost, and safety.', icon: Scale, href: '/tools/peptide-compare', color: '#f59e0b' },
  { title: 'Peptide Stack Builder', desc: 'Build a personalized peptide stack based on your goals.', icon: FlaskConical, href: '/tools/peptide-stack', color: '#00F0FF' },
  { title: 'Reconstitution Guide', desc: 'Step-by-step guide to properly mixing your peptides with BAC water.', icon: Syringe, href: '/tools/reconstitution-guide', color: '#22c55e' },
  { title: 'Peptide Cycle Tracker', desc: 'Track your protocols, doses, and results over time.', icon: Clock, href: '/tools/peptide-cycle', color: '#a78bfa' },
  { title: 'Protocol Tracker', desc: 'Log daily doses, track side effects, and share anonymized data with the community.', icon: Activity, href: '/tools/protocol-tracker', color: '#22c55e' },
];

const FEATURED_SLUGS = [
  'peptide-side-effects-what-the-research-actually-says',
  'the-complete-peptide-guide',
  'bpc-157-dosage-guide',
  'the-wolverine-complex-the-ultimate-peptide-stack-for-rapid-recovery-and-healing',
  'retatrutide-longevity-weapon-epigenetic-reversal-cancer-defense',
];

const POPULAR_PEPTIDES = [
  { name: 'BPC-157', use: 'Gut & Tissue Repair', link: '/blogs/article/bpc-157-dosage-guide', affiliateUrl: 'https://www.americanpeptideresearch.net/product/bpc-157-10mg/ref/16/' },
  { name: 'TB-500', use: 'Cardiac & Tissue Healing', link: '/blogs/article/tb500-heart-health-cardiac-repair', affiliateUrl: 'https://www.americanpeptideresearch.net/product/tb-500-10mg/ref/16/' },
  { name: 'Retatrutide', use: 'Triple Agonist Fat Loss', link: '/blogs/article/retatrutide-longevity-weapon-epigenetic-reversal-cancer-defense', affiliateUrl: 'https://www.americanpeptideresearch.net/product/reta-30mg/ref/16/' },
  { name: 'CJC-1295 + Ipamorelin', use: 'GH Optimization', link: '/blogs/article/cjc-1295-with-dac-ipamorelin-how-these-2-peptides-rebuild-your-body-from-the-inside-out', affiliateUrl: 'https://www.americanpeptideresearch.net/ref/16/' },
  { name: 'MOTS-C', use: 'Exercise Mimetic & Longevity', link: '/blogs/article/mots-c-benefits', affiliateUrl: null },
  { name: 'KPV', use: 'Anti-Inflammatory', link: '/blogs/article/kpv-peptide-master-anti-inflammatory-guide', affiliateUrl: null },
  { name: 'Epitalon', use: 'Telomere Extension', link: '/blogs/article/epitalon-telomere-anti-aging-peptide-guide', affiliateUrl: null },
];

const NAV_SECTIONS = [
  { id: 'overview', label: 'Overview' },
  { id: 'guides', label: 'Guides' },
  { id: 'tools', label: 'Tools' },
  { id: 'peptides', label: 'Peptide Index' },
  { id: 'popular', label: 'Popular' },
  { id: 'faq', label: 'FAQ' },
];

const FAQ = [
  { q: 'What are peptides?', a: 'Peptides are short chains of amino acids (2-50) that act as signaling molecules in the body. Unlike hormones or steroids, they trigger specific biological pathways for recovery, fat loss, cognitive enhancement, or anti-aging without broad systemic effects.' },
  { q: 'Are peptides legal?', a: 'Peptides are legal to purchase as "research chemicals" in most countries. However, they are not FDA-approved for human use (with exceptions like Tesamorelin). They exist in a gray area — legal to buy, but not officially sanctioned as medications. Check your local regulations.' },
  { q: 'What is the best peptide for beginners?', a: 'BPC-157 is considered the safest and most well-tolerated peptide for beginners. It has an excellent safety profile, can be taken orally or via injection, and targets gut health, tissue repair, and inflammation. Start with our BPC-157 Dosage Guide.' },
  { q: 'How do you take peptides?', a: 'Most peptides come as lyophilized (freeze-dried) powder that you reconstitute with bacteriostatic water and inject subcutaneously (under the skin). Some peptides like BPC-157 can be taken orally. See our Reconstitution Guide for step-by-step instructions.' },
  { q: 'Do peptides have side effects?', a: 'Side effects vary by peptide. Common ones include injection site irritation, water retention, and flushing (GHK-Cu). Serious side effects are rare when used at proper doses. Always get baseline bloodwork before starting.' },
  { q: 'How much do peptides cost?', a: 'A typical peptide protocol costs $50-300/month depending on the compounds and doses. Research-grade vials range from $30-80 each. Use code NINJA for 25% off at American Peptide Research.' },
  { q: 'What is the difference between peptides and steroids?', a: 'Peptides are signaling molecules that trigger specific pathways. Steroids are synthetic hormones that directly alter hormone levels. Peptides are more targeted with fewer systemic side effects. Read our detailed comparison: Are Peptides Steroids?' },
];

// ───────────────── Section Nav ─────────────────

const SectionNav = ({ activeSection }) => (
  <div className="sticky top-16 md:top-20 z-40 bg-black/90 backdrop-blur-md border-b border-zinc-800" data-testid="peptides-section-nav">
    <div className="container-ninja py-2 overflow-x-auto">
      <div className="flex items-center gap-1 min-w-max">
        {NAV_SECTIONS.map(s => (
          <a key={s.id} href={`#${s.id}`}
            className={`px-3 py-1.5 text-xs font-heading uppercase tracking-wider transition-colors whitespace-nowrap ${activeSection === s.id ? 'text-[#E63946] border-b-2 border-[#E63946]' : 'text-zinc-500 hover:text-white'}`}>
            {s.label}
          </a>
        ))}
      </div>
    </div>
  </div>
);

// ───────────────── Article Card ─────────────────

const ArticleCard = ({ article, featured }) => (
  <Link
    to={`/blogs/article/${article.slug}`}
    className={`group block border border-zinc-800 hover:border-[#E63946]/40 transition-all duration-300 overflow-hidden bg-zinc-900/30 ${featured ? 'md:col-span-2 md:grid md:grid-cols-2' : ''}`}
    data-testid={`article-card-${article.slug}`}
  >
    {article.featured_image && (
      <div className={`overflow-hidden ${featured ? 'aspect-[16/10]' : 'aspect-[16/9]'}`}>
        <img src={article.featured_image} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
      </div>
    )}
    <div className="p-5">
      {article.category && (
        <span className="text-[10px] uppercase tracking-wider text-[#E63946] font-bold">{article.category}</span>
      )}
      <h3 className="text-white text-sm font-heading mt-1 mb-2 group-hover:text-[#E63946] transition-colors line-clamp-2" style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: featured ? '1.3rem' : '1rem' }}>
        {article.title}
      </h3>
      {article.excerpt && (
        <p className="text-zinc-500 text-xs line-clamp-2 mb-3">{article.excerpt}</p>
      )}
      <div className="flex items-center gap-3 text-xs text-zinc-600">
        {article.read_time > 0 && <span>{article.read_time} min read</span>}
        {article.view_count > 0 && <span>{article.view_count} views</span>}
      </div>
    </div>
  </Link>
);

// ───────────────── Main Page ─────────────────

const PeptidesHubPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [articles, setArticles] = useState([]);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [activeSection, setActiveSection] = useState('overview');

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const res = await articlesApi.getAll({ category: 'Peptides', published: true });
        setArticles(res.data || []);
      } catch (e) { console.error(e); }
    };
    fetchArticles();
  }, []);

  // Scroll spy
  useEffect(() => {
    const handleScroll = () => {
      const sections = NAV_SECTIONS.map(s => ({ id: s.id, el: document.getElementById(s.id) })).filter(s => s.el);
      let current = 'overview';
      for (const s of sections) {
        if (window.scrollY >= s.el.offsetTop - 140) current = s.id;
      }
      setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getArticlesForCategory = (catId) => {
    const slugs = ARTICLE_CATEGORIES[catId] || [];
    return slugs.map(slug => articles.find(a => a.slug === slug)).filter(Boolean);
  };

  const filteredArticles = (() => {
    let list = activeCategory === 'all'
      ? [...articles]
      : getArticlesForCategory(activeCategory);
    // Always pin the featured slug to position 0
    const pinnedSlug = FEATURED_SLUGS[0];
    const idx = list.findIndex(a => a.slug === pinnedSlug);
    if (idx > 0) {
      const [pinned] = list.splice(idx, 1);
      list = [pinned, ...list];
    }
    return list;
  })();

  const featuredArticles = FEATURED_SLUGS.map(s => articles.find(a => a.slug === s)).filter(Boolean);
  const mostViewed = [...articles].sort((a, b) => (b.view_count || 0) - (a.view_count || 0)).slice(0, 6);

  return (
    <main className="pt-20 min-h-screen" data-testid="peptides-hub-page">
      <PageSEO
        title="Peptide Hub — Guides, Protocols & Tools | NinjAthlete"
        description="The complete peptide resource. BPC-157, Retatrutide, TB-500 and more. Dosing guides, stacking protocols, cycle tracking tools, and the science behind every compound."
        path="/peptides"
        breadcrumbs={[{ name: 'Home', url: '/' }, { name: 'Peptides Hub', url: '/peptides' }]}
      />

      {/* Hero */}
      <section id="overview" className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-[#0a0a0a] to-black" />
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle at 30% 30%, rgba(230,57,70,0.4) 0%, transparent 50%), radial-gradient(circle at 70% 70%, rgba(0,240,255,0.25) 0%, transparent 50%)' }} />
        <div className="container-ninja relative z-10 py-16 md:py-24">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#E63946] text-white text-xs font-heading tracking-widest uppercase mb-6" data-testid="peptides-hub-badge">
              <FlaskConical className="w-3.5 h-3.5" /> Peptide Encyclopedia
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl text-white font-heading leading-[0.95] mb-5" data-testid="peptides-hub-title">
              THE COMPLETE <span className="text-[#E63946]">PEPTIDE</span> HUB
            </h1>
            <p className="text-zinc-400 text-lg md:text-xl max-w-xl mb-6">
              Every compound explained. Dosing protocols, stacking strategies, blood work guides, and interactive tools — backed by science, built for athletes.
            </p>
            <div className="flex items-center gap-4 text-sm flex-wrap">
              <span className="text-white font-heading">{articles.length} guides</span>
              <span className="text-zinc-600">|</span>
              <span className="text-zinc-400">{CATEGORIES.length} categories</span>
              <span className="text-zinc-600">|</span>
              <span className="text-zinc-400">{PEPTIDE_TOOLS.length} interactive tools</span>
            </div>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/blogs/article/the-complete-peptide-guide" className="inline-flex items-center gap-2 px-6 py-3 bg-[#E63946] text-white font-heading text-sm uppercase tracking-wider hover:opacity-90 transition-opacity" data-testid="peptide-guide-cta">
                Read the Master Guide <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/tools/peptide-stack" className="inline-flex items-center gap-2 px-6 py-3 border border-zinc-700 text-zinc-300 font-heading text-sm uppercase tracking-wider hover:border-white hover:text-white transition-colors" data-testid="stack-builder-cta">
                Build Your Stack <FlaskConical className="w-4 h-4" />
              </Link>
              <a href="https://peptiq.io/go/ninja" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 border border-[#00F0FF]/40 text-[#00F0FF] font-heading text-sm uppercase tracking-wider hover:border-[#00F0FF] hover:bg-[#00F0FF]/5 transition-colors" data-testid="peptiq-download-cta">
                Download PeptIQ App <Download className="w-4 h-4" />
              </a>
              <ShareButton title="The Complete Peptide Hub | NinjAthlete" path="/peptides" accentColor="#E63946" />
            </div>
          </div>
        </div>
      </section>

      <SectionNav activeSection={activeSection} />

      <div className="container-ninja pt-10">
        <PeptiCoolBanner utmContent="peptides_hub_card" compact />
      </div>

      {/* Category Filter + Guides */}
      <section id="guides" className="bg-[#050505] py-14 md:py-20">
        <div className="container-ninja">
          <div className="flex items-center justify-between mb-8">
            <div>
              <p className="text-[#E63946] text-xs uppercase tracking-widest font-bold mb-1">Explore by Category</p>
              <h2 className="text-white font-heading text-2xl md:text-3xl">Peptide Guides & Protocols</h2>
            </div>
            <span className="text-zinc-500 text-sm hidden md:block">{filteredArticles.length} articles</span>
          </div>

          {/* Category Pills */}
          <div className="flex flex-wrap gap-2 mb-10">
            <button onClick={() => setActiveCategory('all')}
              className={`px-4 py-2 text-xs font-heading uppercase tracking-wider border transition-all ${activeCategory === 'all' ? 'text-white border-white/30 bg-white/5' : 'text-zinc-500 border-zinc-800 hover:text-white hover:border-zinc-600'}`}
              data-testid="filter-all">
              All ({articles.length})
            </button>
            {CATEGORIES.map(c => {
              const count = getArticlesForCategory(c.id).length;
              return (
                <button key={c.id} onClick={() => setActiveCategory(c.id)}
                  className={`px-4 py-2 text-xs font-heading uppercase tracking-wider border transition-all flex items-center gap-2 ${activeCategory === c.id ? `text-white border-opacity-40 bg-opacity-10` : 'text-zinc-500 border-zinc-800 hover:text-white hover:border-zinc-600'}`}
                  style={activeCategory === c.id ? { borderColor: c.color, color: c.color, background: `${c.color}10` } : {}}
                  data-testid={`filter-${c.id}`}>
                  <c.icon className="w-3.5 h-3.5" /> {c.label} ({count})
                </button>
              );
            })}
          </div>

          {/* Article Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredArticles.map((article, idx) => (
              <ArticleCard key={article.slug} article={article} featured={idx === 0 && activeCategory === 'all'} />
            ))}
          </div>

          {filteredArticles.length === 0 && articles.length > 0 && (
            <p className="text-zinc-500 text-center py-12">No articles in this category yet.</p>
          )}
        </div>
      </section>

      {/* Interactive Tools */}
      <section id="tools" className="bg-black py-14 md:py-20 border-t border-zinc-900">
        <div className="container-ninja">
          <div className="mb-10">
            <p className="text-[#00F0FF] text-xs uppercase tracking-widest font-bold mb-1">Interactive</p>
            <h2 className="text-white font-heading text-2xl md:text-3xl">Peptide Tools & Calculators</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PEPTIDE_TOOLS.map(tool => (
              <Link key={tool.href} to={tool.href}
                className="group block p-6 border border-zinc-800 hover:border-opacity-50 transition-all duration-300 bg-zinc-900/20"
                style={{ '--tool-color': tool.color }}
                data-testid={`tool-${tool.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="w-10 h-10 flex items-center justify-center border border-zinc-700 mb-4 group-hover:border-current transition-colors" style={{ color: tool.color }}>
                  <tool.icon className="w-5 h-5" />
                </div>
                <h3 className="text-white font-heading text-lg mb-2 group-hover:text-current transition-colors" style={{ '--tw-text-opacity': 1 }}>
                  <span style={{ color: 'inherit' }} className="group-hover:text-[color:var(--tool-color)]">{tool.title}</span>
                </h3>
                <p className="text-zinc-500 text-sm">{tool.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Peptide Quick Reference Index */}
      <section id="peptides" className="bg-[#050505] py-14 md:py-20 border-t border-zinc-900">
        <div className="container-ninja">
          <div className="mb-10">
            <p className="text-[#E63946] text-xs uppercase tracking-widest font-bold mb-1">Quick Reference</p>
            <h2 className="text-white font-heading text-2xl md:text-3xl">Peptide Index</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {POPULAR_PEPTIDES.map(p => (
              <div key={p.name} className="border border-zinc-800 p-5 bg-zinc-900/20 hover:border-zinc-700 transition-colors" data-testid={`peptide-index-${p.name.toLowerCase().replace(/\s+/g, '-')}`}>
                <h4 className="text-white font-heading text-lg mb-1">{p.name}</h4>
                <p className="text-zinc-500 text-xs mb-3">{p.use}</p>
                <div className="flex flex-wrap gap-2">
                  <Link to={p.link} className="text-[10px] uppercase tracking-wider text-[#00F0FF] border border-[#00F0FF]/20 px-2 py-1 hover:bg-[#00F0FF]/10 transition-colors">
                    Read Guide
                  </Link>
                  {p.affiliateUrl && (
                    <a href={p.affiliateUrl} target="_blank" rel="sponsored noopener" className="text-[10px] uppercase tracking-wider text-[#E63946] border border-[#E63946]/20 px-2 py-1 hover:bg-[#E63946]/10 transition-colors flex items-center gap-1">
                      Buy <ExternalLink className="w-2.5 h-2.5" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 text-center">
            <p className="text-zinc-600 text-xs">Your discount is applied automatically through our <a href="https://www.americanpeptideresearch.net/ref/16/" target="_blank" rel="sponsored noopener" className="text-[#E63946] hover:underline">American Peptide Research</a> links</p>
          </div>
        </div>
      </section>

      {/* APR Product Showcase */}
      <section id="shop-apr" className="bg-black py-14 md:py-20 border-t border-zinc-900">
        <div className="container-ninja">
          <div className="mb-2">
            <p className="text-[#22c55e] text-xs uppercase tracking-widest font-bold mb-1">Shop</p>
          </div>
          <AprProductGrid
            productKeys={APR_TOP_SELLERS}
            limit={8}
            title="Best-Selling Research Peptides"
            subtitle="Third-party tested, made in the USA. Discount applied automatically at checkout through our links."
          />
        </div>
      </section>

      {/* Most Popular Articles */}
      <section id="popular" className="bg-black py-14 md:py-20 border-t border-zinc-900">
        <div className="container-ninja">
          <div className="mb-10">
            <p className="text-[#FFD100] text-xs uppercase tracking-widest font-bold mb-1">Trending</p>
            <h2 className="text-white font-heading text-2xl md:text-3xl">Most Popular Peptide Content</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {mostViewed.map((article, idx) => (
              <Link key={article.slug} to={`/blogs/article/${article.slug}`}
                className="group flex gap-4 items-start p-4 border border-zinc-800 hover:border-[#E63946]/30 transition-colors bg-zinc-900/20"
                data-testid={`popular-${article.slug}`}>
                <span className="text-2xl font-heading text-zinc-700 group-hover:text-[#E63946] transition-colors flex-shrink-0 w-8" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                  {String(idx + 1).padStart(2, '0')}
                </span>
                <div className="min-w-0">
                  <h4 className="text-white text-sm font-medium group-hover:text-[#E63946] transition-colors line-clamp-2 font-heading" style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: '.95rem' }}>
                    {article.title}
                  </h4>
                  <div className="flex items-center gap-3 mt-1 text-xs text-zinc-600">
                    {article.view_count > 0 && <span className="flex items-center gap-1"><TrendingUp className="w-3 h-3" /> {article.view_count} views</span>}
                    {article.read_time > 0 && <span>{article.read_time} min</span>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-[#050505] py-14 md:py-20 border-t border-zinc-900">
        <div className="container-ninja max-w-3xl">
          <div className="text-center mb-10">
            <p className="text-[#00F0FF] text-xs uppercase tracking-widest font-bold mb-1">Common Questions</p>
            <h2 className="text-white font-heading text-2xl md:text-3xl">Peptide FAQ</h2>
          </div>
          <div className="space-y-3">
            {FAQ.map((item, idx) => (
              <div key={item.q} className={`border transition-colors ${expandedFaq === idx ? 'border-[#E63946]/30' : 'border-zinc-800'}`} data-testid={`faq-${idx}`}>
                <button
                  onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between p-5 text-left bg-zinc-900/40 hover:bg-zinc-900/60 transition-colors"
                >
                  <span className="text-white text-sm font-medium pr-4">{item.q}</span>
                  <ChevronDown className={`w-4 h-4 text-zinc-500 flex-shrink-0 transition-transform ${expandedFaq === idx ? 'rotate-180 text-[#E63946]' : ''}`} />
                </button>
                {expandedFaq === idx && (
                  <div className="px-5 pb-5 text-zinc-400 text-sm leading-relaxed bg-zinc-900/20">
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-black py-14 md:py-20 border-t border-zinc-900">
        <div className="container-ninja text-center">
          <h2 className="text-white font-heading text-2xl md:text-3xl mb-4">Ready to Start Your Protocol?</h2>
          <p className="text-zinc-400 max-w-lg mx-auto mb-8">Use code <strong className="text-[#FFD100]">NINJA</strong> for 25% off research-grade peptides from American Peptide Research.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="https://www.americanpeptideresearch.net/ref/16/" target="_blank" rel="sponsored noopener"
              className="inline-flex items-center gap-2 px-8 py-3 bg-[#E63946] text-white font-heading text-sm uppercase tracking-wider hover:opacity-90 transition-opacity">
              Shop Peptides <ExternalLink className="w-4 h-4" />
            </a>
            <Link to="/blogs/article/blood-tests-before-peptides-the-complete-pre-protocol-lab-guide"
              className="inline-flex items-center gap-2 px-8 py-3 border border-zinc-700 text-zinc-300 font-heading text-sm uppercase tracking-wider hover:border-white hover:text-white transition-colors">
              Get Blood Work First <ShieldCheck className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* PeptIQ App Download CTA */}
      <section className="bg-[#050505] py-14 md:py-20 border-t border-zinc-900" data-testid="peptiq-promo-section">
        <div className="container-ninja">
          <div className="max-w-3xl mx-auto bg-gradient-to-br from-zinc-900 to-zinc-900/50 border border-[#00F0FF]/20 p-8 md:p-12">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-10">
              <div className="flex-1">
                <p className="text-[#00F0FF] text-xs uppercase tracking-widest font-bold mb-2">AI-Powered Tracking</p>
                <h3 className="text-white font-heading text-xl md:text-2xl mb-3">Track Your Peptide Protocol with PeptIQ</h3>
                <p className="text-zinc-400 text-sm leading-relaxed mb-4">Smart dosing reminders, protocol logging, and AI-powered guidance — all in one app. Never miss a dose again.</p>
                <div className="flex items-center gap-2 px-3 py-2 bg-black/40 border border-[#E63946]/20 w-fit mb-5">
                  <span className="text-xs text-zinc-300">Use code <span className="text-[#E63946] font-bold">NINJA</span> for <span className="text-white font-bold">20% off annual</span> — $80/yr (was $100)</span>
                </div>
                <div className="flex flex-wrap gap-3">
                  <a href="https://peptiq.io/go/ninja" target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-[#00F0FF] text-black font-heading text-sm uppercase tracking-wider hover:opacity-90 transition-opacity"
                    data-testid="peptiq-promo-download">
                    Download PeptIQ <Download className="w-4 h-4" />
                  </a>
                  <a href="https://peptiq.io/partners" target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 border border-zinc-700 text-zinc-300 font-heading text-sm uppercase tracking-wider hover:border-white hover:text-white transition-colors"
                    data-testid="peptiq-partners-link">
                    Partner Program <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="container-ninja">
          <LocatorFeeder
            heading="Find peptide & recovery clinics near you"
            blurb="Peptide therapy, NAD+ and hormone clinics — 540+ vetted facilities across 25 metros in our free Recovery Locator."
            therapies={[
              { slug: 'peptide-therapy', name: 'Peptide Therapy' },
              { slug: 'nad-iv', name: 'NAD+ Therapy' },
              { slug: 'hormone-therapy', name: 'Hormone / TRT' },
            ]}
            testId="peptides-locator-feeder"
          />
        </div>
      </section>
    </main>
  );
};

export default PeptidesHubPage;
